"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import { Facebook, Instagram, Twitter, Mail, MapPin, Phone, ChevronDown } from "lucide-react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSnapchat, faTiktok } from "@fortawesome/free-brands-svg-icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { useGetPublicSettingsQuery } from "@/store"

export default function Footer() {
  const { data: publicSettings } = useGetPublicSettingsQuery();
  const businessInfo = publicSettings?.data || {};
  
  // Silently handle errors - use defaults if settings aren't available
  // This prevents the app from breaking if settings haven't been initialized yet
  const defaultBrand = "Zest and Zeal";
  const defaultTagline = "24 hour online food delivery. The best hand-crafted rice & pasta meals made fresh to order.";
  const defaultAddress = "Lagos";
  const defaultPhone = "09017104173";
  const defaultPhoneHours = "Mon - Sun:";
  const defaultEmail = "zestandzeallunches@gmail.com";

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    Information: true,
    Extras: true,
  })

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  // Format business hours
  const formatBusinessHours = (hours: Record<string, string> | undefined) => {
    if (!hours || Object.keys(hours).length === 0) return defaultPhoneHours
    return Object.entries(hours)
      .map(([day, h]) => `${day.charAt(0).toUpperCase() + day.slice(1)}: ${h}`)
      .join("\n")
  }

  const phoneHours = formatBusinessHours(businessInfo.businessHours)

  // Get social media links
  const socialMedia = businessInfo.socialMedia || {};
  const socialLinks = [
    { name: "Facebook", icon: Facebook, url: socialMedia.facebook },
    { name: "Instagram", icon: Instagram, url: socialMedia.instagram },
    { name: "Twitter", icon: Twitter, url: socialMedia.twitter },
    {
      name: "Snapchat",
      icon: ({ className }: { className?: string }) => (
        <FontAwesomeIcon icon={faSnapchat} className={className} />
      ),
      url: socialMedia.snapchat,
    },
    {
      name: "TikTok",
      icon: ({ className }: { className?: string }) => (
        <FontAwesomeIcon icon={faTiktok} className={className} />
      ),
      url: socialMedia.tiktok,
    },
  ].filter((link) => link.url); // Only show links that exist

  const footerLinks = {
    Information: ["Home", "Our Menu", "Testimonials", "Terms of Service", "Privacy Policy", "Refund Policy"],
    Extras: ["Track Order", "My Cart", "Checkout", "Contact Us"],
  }

  return (
    <footer className="border-t border-border bg-card">
      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Top Section */}
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          {/* Brand & Newsletter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-4"
          >
            <div className="mb-6 flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                <span className="font-mono text-xl font-bold text-primary-foreground">ZZ</span>
              </div>
              <span className="text-2xl font-bold tracking-tight text-foreground">
                {businessInfo.name || defaultBrand}
              </span>
            </div>
            <p className="mb-6 text-pretty leading-relaxed text-muted-foreground">
              {businessInfo.description || defaultTagline}
            </p>

            <div className="mb-6">
              <h4 className="mb-3 text-sm font-semibold text-foreground">Stay Updated</h4>
              <div className="flex gap-2">
                <Input type="email" placeholder="Enter your email" className="flex-1" />
                <Button size="icon" className="shrink-0">
                  <Mail className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {socialLinks.length > 0 && (
              <div className="flex gap-3">
                {socialLinks.map(({ name, icon: Icon, url }) => (
                  <motion.div key={name} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full bg-transparent"
                      asChild
                    >
                      <a href={url} target="_blank" rel="noopener noreferrer" aria-label={name}>
                        <Icon className="h-4 w-4" />
                      </a>
                    </Button>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Links Sections */}
          {Object.entries(footerLinks).map(([title, links], sectionIndex) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
              className="lg:col-span-2"
            >
              <Collapsible
                open={openSections[title]}
                onOpenChange={() => toggleSection(title)}
                className="w-full"
              >
                <CollapsibleTrigger className="flex w-full items-center justify-between md:cursor-default md:pointer-events-none md:select-none">
                  <h4 className="text-sm font-semibold text-foreground">{title}</h4>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 md:hidden ${
                      openSections[title] ? "rotate-180" : ""
                    }`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up md:!block md:!animate-none">
                  <ul className="space-y-3 mt-4 md:mt-4">
                    {links.map((link) => {
                      let href = "#"
                      if (link === "Home") href = "/"
                      else if (link === "Our Menu") href = "/our-menu"
                      else if (link === "Testimonials") href = "/testimonials"
                      else if (link === "Terms of Service") href = "/terms-of-service"
                      else if (link === "Privacy Policy") href = "/privacy-policy"
                      else if (link === "Refund Policy") href = "/refund-policy"
                      else if (link === "Contact Us") href = "/contact"
                      else if (link === "Track Order") href = "/track-order"
                      else if (link === "My Cart") href = "/cart"
                      else if (link === "Checkout") href = "/checkout"
                      return (
                        <motion.li key={link} whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
                          <Link
                            to={href}
                            className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                          >
                            {link}
                          </Link>
                        </motion.li>
                      )
                    })}
                  </ul>
                </CollapsibleContent>
              </Collapsible>
            </motion.div>
          ))}
        </div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 border-t border-border pt-8"
        >
          <div className="grid gap-6 md:grid-cols-3">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-gradient-to-r from-orange-500/10 to-amber-500/10 p-2">
                <MapPin className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <div className="mb-1 text-sm font-semibold text-foreground">Address</div>
                <div className="whitespace-pre-line text-sm text-muted-foreground">
                  {businessInfo.address || defaultAddress}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-gradient-to-r from-orange-500/10 to-amber-500/10 p-2">
                <Phone className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <div className="mb-1 text-sm font-semibold text-foreground">Phone</div>
                <div className="text-sm text-muted-foreground whitespace-pre-line">
                  {businessInfo.phone || defaultPhone}
                  <br />
                  {phoneHours}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-gradient-to-r from-orange-500/10 to-amber-500/10 p-2">
                <Mail className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <div className="mb-1 text-sm font-semibold text-foreground">Email</div>
                <div className="text-sm text-muted-foreground">
                  {businessInfo.email || defaultEmail}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 border-t border-border pt-8"
        >
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <p className="text-sm text-muted-foreground">
              {businessInfo.name || defaultBrand} © {new Date().getFullYear()} All rights reserved!
            </p>
            <div className="flex flex-wrap items-center gap-6">
              <Link to="/privacy-policy" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                Privacy
              </Link>
              <Link to="/terms-of-service" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                Terms
              </Link>
              <a href="#" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                Cookies
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  )
}
